import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  TextField,
  Alert,
  CircularProgress,
  Checkbox,
  Radio,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Divider,
  Snackbar,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SaveIcon from "@mui/icons-material/Save";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LockIcon from "@mui/icons-material/Lock";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HistoryIcon from "@mui/icons-material/History";

import Navbar from "../navbar";
import { getPatientMedicalHistory, savePatientMedicalHistory } from "../../service/patienthistory";
import { getClinicalObservation, saveClinicalObservation, getTreatmentHistory } from "../../service/clinicalobservation";
import { listAppointments } from "../../service/appointment";
import { getAssignedAssessmentsByCustomer } from "../../service/customerassessment";
import QUESTIONNAIRES from '../Assessments/questionnaires';
import { getCustomer } from "../../service/customer";
import AssessmentDetails from "../counsellor/AssessmentDetails";
import PatientDetails from "../counsellor/PatientDetails";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const THEME = {
  blue: { main: "#2563eb", light: "#eff6ff", border: "#bfdbfe", text: "#1e40af" },
  purple: { main: "#6d28d9", light: "#f5f3ff", border: "#ddd6fe", text: "#5b21b6" },
  green: { main: "#16a34a", light: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
  slate: { main: "#475569", light: "#f8fafc", border: "#e2e8f0", text: "#334155" },
  amber: { main: "#d97706", light: "#fffbeb", border: "#fde68a", text: "#b45309" },
  red: { main: "#dc2626", light: "#fef2f2", border: "#fecaca", text: "#b91c1c" },
  neutral: { main: "#1f2937", light: "#f9fafb", border: "#e5e7eb", text: "#374151" },
};

// ─── Utility ──────────────────────────────────────────────────────────────────
const normalizePayload = (obj) => {
  const cleaned = {};
  Object.entries(obj).forEach(([key, value]) => {
    cleaned[key] = typeof value === "string" && value.trim() === "" ? null : value;
  });
  return cleaned;
};

// ─── Shared field label ───────────────────────────────────────────────────────
const FieldLabel = ({ text, required }) => (
  <Typography sx={{ fontWeight: 600, color: "#374151", mb: 0.5, fontSize: "0.78rem", letterSpacing: "0.01em" }}>
    {text}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </Typography>
);

// ─── Compact TextField styles ─────────────────────────────────────────────────
const fieldSx = (locked = false, accent = "#6366f1") => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "7px",
    backgroundColor: locked ? "#f9fafb" : "#fff",
    "& textarea, & input": {
      fontSize: "0.82rem",
      color: "#1f2937",
      lineHeight: 1.5,
      padding: "6px 10px",
    },
    "& fieldset": { borderColor: locked ? "#e5e7eb" : "#d1d5db" },
    "&:hover fieldset": { borderColor: "#9ca3af" },
    "&.Mui-focused fieldset": { borderColor: accent, borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input::placeholder": { color: "#a1aab5", opacity: 1, fontSize: "0.79rem" },
  "& .MuiFormHelperText-root": { ml: 0, mt: 0.4, fontSize: "0.72rem" },
});

// ─── Read-Only Field Display ──────────────────────────────────────────────────
const ReadOnlyField = ({ label, value, accent = "default", required }) => {
  const isEmpty = !value || (typeof value === "string" && value.trim() === "");

  const labelColor = {
    blue: "#2563eb",
    green: "#15803d",
    purple: "#5b21b6",
    default: "#374151",
  }[accent] || "#374151";

  return (
    <Box sx={{ py: 0.25 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.78rem", color: "#6b7280", mb: 0.35, letterSpacing: "0.01em" }}>
        {label}
        {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
      </Typography>
      {isEmpty ? (
        <Typography sx={{ fontSize: "0.82rem", color: "#c0c4cc", fontStyle: "italic" }}>—</Typography>
      ) : (
        <Typography sx={{ fontSize: "0.82rem", color: "#1f2937", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
          {value}
        </Typography>
      )}
    </Box>
  );
};

// ─── Read-Only Medical History Section ───────────────────────────────────────
const ReadOnlyMedicalHistory = ({ formData, fields }) => {
  const rows = [];
  let i = 0;
  while (i < fields.length) {
    const f = fields[i];
    if (f.full) {
      rows.push([f]);
      i++;
    } else {
      const next = fields[i + 1] && !fields[i + 1].full ? fields[i + 1] : null;
      rows.push(next ? [f, next] : [f]);
      i += next ? 2 : 1;
    }
  }
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
        <LockIcon sx={{ fontSize: "0.75rem", color: "#9ca3af" }} />
        <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af" }}>
          Case history has been saved and is locked from further edits.
        </Typography>
      </Box>
      {rows.map((row, rowIdx) => (
        <Box key={rowIdx} sx={{ mb: 2 }}>
          <Grid container columnSpacing={4}>
            {row.map((f) => (
              <Grid item xs={12} md={row.length === 1 ? 12 : 6} key={f.key}>
                <ReadOnlyField
                  label={f.label}
                  value={formData[f.key]}
                  required={f.required}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};


// ─── Collapsible Section ──────────────────────────────────────────────────────
const CollapsibleSection = ({ title, icon, accentColor = "#2563eb", children, badge, defaultOpen = false, rightContent }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box
      sx={{
        mb: 1.75,
        border: "1px solid #e9ecef",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.25,
          py: 1.25,
          cursor: "pointer",
          bgcolor: open ? "#fafbfc" : "#fff",
          borderBottom: open ? "1px solid #e9ecef" : "none",
          transition: "background 0.12s",
          "&:hover": { bgcolor: "#f4f6f8" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              bgcolor: `${accentColor}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: accentColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1f2937" }}>{title}</Typography>
          {badge && (
            <Chip
              label={badge}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: `${accentColor}14`,
                color: accentColor,
                border: `1px solid ${accentColor}30`,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}
        </Box>
        <Box sx={{ color: "#9ca3af", display: "flex", alignItems: "center", gap: 1.5 }}>
          {rightContent}
          {open ? <ExpandLessIcon sx={{ fontSize: "1.1rem" }} /> : <ExpandMoreIcon sx={{ fontSize: "1.1rem" }} />}
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 2.25 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

// ─── Treatment Plan Modal ─────────────────────────────────────────────────────
const TreatmentPlanModal = ({ open, onClose, initialData, onSave, patientName }) => {
  const [form, setForm] = useState({
    working_diagnosis: "",
    treatment_goals: "",
    recommended_intervention: "",
    recommended_frequency: "",
    treatment_objectives: "",
    follow_up_plan: "",
    additional_notes: "",
    triage_level: "",
  });

  useEffect(() => {
    if (initialData) setForm({ ...form, ...initialData });
  }, [initialData, open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const requiredTreatmentFields = [
    'working_diagnosis', 'treatment_goals', 'recommended_intervention',
    'recommended_frequency', 'treatment_objectives', 'follow_up_plan', 'triage_level',
  ];
  const isTreatmentPlanComplete = requiredTreatmentFields.every(
    (f) => form[f] && String(form[f]).trim() !== ''
  );

  const interventions = [
    "Cognitive Behavioral Therapy (CBT)",
    "Dialectical Behavior Therapy (DBT)",
    "EMDR",
    "Psychodynamic Therapy",
    "Supportive Therapy",
    "Behavioural Activation (BA)",
    "Grief-Focused CBT",
    "Combined Approach",
    "Other",
  ];
  const frequencies = ["Weekly", "Bi-weekly", "Monthly", "As needed"];

  const triageOptions = [
    { value: "green", label: "Green", sub: "No Risk", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
    { value: "amber", label: "Amber", sub: "Mild Case", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    { value: "red", label: "Red", sub: "High Risk", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
    { value: "black", label: "Black", sub: "Critical", color: "#1f2937", bg: "#f9fafb", border: "#d1d5db" },
  ];

  const modalFieldSx = fieldSx(false, "#16a34a");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalHospitalIcon sx={{ color: "#fff", fontSize: "1.1rem" }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }}>
              Treatment Plan
            </Typography>
            {patientName && (
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.75rem" }}>
                {patientName}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, overflowY: "auto" }}>
        <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Working Diagnosis */}
          <Box>
            <FieldLabel text="Working Diagnosis" required />
            <TextField
              fullWidth
              placeholder="e.g., Major Depressive Disorder (MDD), Moderate Severity..."
              value={form.working_diagnosis}
              onChange={(e) => set("working_diagnosis", e.target.value)}
              sx={modalFieldSx}
            />
          </Box>

          {/* Treatment Goals */}
          <Box>
            <FieldLabel text="Treatment Goals" required />
            <TextField
              fullWidth multiline rows={3}
              placeholder={"1. Reduce PHQ-9 score from 18 to below 10 within 10 sessions\n2. Re-engage in daily activities and restore functional routine\n3. Process grief and develop adaptive coping with loss"}
              value={form.treatment_goals}
              onChange={(e) => set("treatment_goals", e.target.value)}
              sx={modalFieldSx}
            />
          </Box>

          {/* Intervention Type + Session Frequency */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Intervention Type" required />
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={form.recommended_intervention}
                  onChange={(e) => set("recommended_intervention", e.target.value)}
                  renderValue={(val) => val || "Select..."}
                  sx={{
                    borderRadius: "7px",
                    fontSize: "0.82rem",
                    bgcolor: "#fff",
                    "& .MuiSelect-select": { py: "7px", color: form.recommended_intervention ? "#1f2937" : "#a1aab5" },
                    "& fieldset": { borderColor: "#d1d5db" },
                    "&:hover fieldset": { borderColor: "#9ca3af" },
                    "&.Mui-focused fieldset": { borderColor: "#16a34a", borderWidth: "1.5px" },
                  }}
                >
                  {interventions.map((i) => (
                    <MenuItem key={i} value={i} sx={{ fontSize: "0.82rem" }}>{i}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Session Frequency" required />
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={form.recommended_frequency}
                  onChange={(e) => set("recommended_frequency", e.target.value)}
                  renderValue={(val) => val || "Select..."}
                  sx={{
                    borderRadius: "7px",
                    fontSize: "0.82rem",
                    bgcolor: "#fff",
                    "& .MuiSelect-select": { py: "7px", color: form.recommended_frequency ? "#1f2937" : "#a1aab5" },
                    "& fieldset": { borderColor: "#d1d5db" },
                    "&:hover fieldset": { borderColor: "#9ca3af" },
                    "&.Mui-focused fieldset": { borderColor: "#16a34a", borderWidth: "1.5px" },
                  }}
                >
                  {frequencies.map((f) => (
                    <MenuItem key={f} value={f} sx={{ fontSize: "0.82rem" }}>{f}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Treatment Objectives */}
          <Box>
            <FieldLabel text="Treatment Objectives" required />
            <TextField
              fullWidth multiline rows={3}
              placeholder="Psychoeducation on depression and grief; Behavioural Activation to restore pleasurable activities..."
              value={form.treatment_objectives}
              onChange={(e) => set("treatment_objectives", e.target.value)}
              sx={modalFieldSx}
            />
          </Box>

          {/* Follow-up Plan */}
          <Box>
            <FieldLabel text="Follow-up Plan" required />
            <TextField
              fullWidth multiline rows={2}
              placeholder="Review PHQ-9 at session 6. If insufficient progress, consider psychiatric referral..."
              value={form.follow_up_plan}
              onChange={(e) => set("follow_up_plan", e.target.value)}
              sx={modalFieldSx}
            />
          </Box>

          {/* Additional Notes */}
          <Box>
            <FieldLabel text="Additional Notes" />
            <TextField
              fullWidth multiline rows={2}
              placeholder="Any additional clinical notes, context, or observations..."
              value={form.additional_notes}
              onChange={(e) => set("additional_notes", e.target.value)}
              sx={modalFieldSx}
            />
          </Box>

          {/* Triage Colour Coding */}
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "10px", p: 2, bgcolor: "#fafafa" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "5px",
                  bgcolor: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalHospitalIcon sx={{ color: "#fff", fontSize: "0.7rem" }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "#374151" }}>
                Triage Colour Coding
              </Typography>
            </Box>
            <Grid container spacing={1.25}>
              {triageOptions.map((opt) => {
                const selected = form.triage_level === opt.value;
                return (
                  <Grid item xs={6} sm={3} key={opt.value}>
                    <Box
                      onClick={() => set("triage_level", opt.value)}
                      sx={{
                        border: `1.5px solid ${selected ? opt.color : "#e5e7eb"}`,
                        borderRadius: "10px",
                        p: 1.25,
                        cursor: "pointer",
                        bgcolor: selected ? opt.bg : "#fff",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                        transition: "all 0.14s",
                        "&:hover": { borderColor: opt.border, bgcolor: opt.bg },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          bgcolor: selected ? opt.color : `${opt.color}18`,
                          border: `2px solid ${selected ? opt.color : `${opt.color}40`}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {opt.value === "green" && <CheckIcon sx={{ fontSize: "1rem", color: selected ? "#fff" : opt.color }} />}
                        {opt.value === "amber" && <WarningAmberIcon sx={{ fontSize: "1rem", color: selected ? "#fff" : opt.color }} />}
                        {opt.value === "red" && <WarningAmberIcon sx={{ fontSize: "1rem", color: selected ? "#fff" : opt.color }} />}
                        {opt.value === "black" && <CloseIcon sx={{ fontSize: "1rem", color: selected ? "#fff" : opt.color }} />}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: opt.color }}>{opt.label}</Typography>
                      <Typography sx={{ fontSize: "0.68rem", color: "#6b7280" }}>{opt.sub}</Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderTop: "1px solid #e5e7eb",
          bgcolor: "#fafafa",
        }}
      >
        {!isTreatmentPlanComplete && (
          <Typography sx={{ fontSize: "0.72rem", color: "#d97706", textAlign: "right" }}>
            * Please fill all required fields to save.
          </Typography>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.82rem",
            borderRadius: "8px",
            px: 2.5,
            py: 0.85,
            borderColor: "#d1d5db",
            color: "#374151",
            "&:hover": { borderColor: "#9ca3af", bgcolor: "#f3f4f6" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => { onSave(form); onClose(); }}
          disabled={!isTreatmentPlanComplete}
          startIcon={<SaveIcon sx={{ fontSize: "0.95rem !important" }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.82rem",
            borderRadius: "8px",
            px: 2.5,
            py: 0.85,
            bgcolor: "#16a34a",
            boxShadow: "none",
            "&:hover": { bgcolor: "#15803d", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "#d1d5db", color: "#9ca3af" },
          }}
        >
          Save Treatment Plan
        </Button>
        </Box>  {/* inner flex row */}
      </Box>  {/* footer */}
    </Dialog>
  );
};

// ─── Saved Treatment Plan View ────────────────────────────────────────────────
const TreatmentPlanView = ({ data, onEdit }) => {
  const triageConfig = {
    green: { label: "Green", sub: "No Risk", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
    amber: { label: "Amber", sub: "Mild Case", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    red: { label: "Red", sub: "High Risk", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
    black: { label: "Black", sub: "Critical", color: "#1f2937", bg: "#f9fafb", border: "#d1d5db" },
  };
  const triage = data.triage_level ? triageConfig[data.triage_level] : null;

  const Label = ({ children }) => (
    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#1f2937", mb: 0.5 }}>
      {children}
    </Typography>
  );

  const Value = ({ children }) =>
    children ? (
      <Typography sx={{ fontSize: "0.82rem", color: "#374151", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
        {children}
      </Typography>
    ) : (
      <Typography sx={{ fontSize: "0.82rem", color: "#9ca3af", fontStyle: "italic" }}>—</Typography>
    );

  const Cell = ({ label, value, sx: extraSx }) => (
    <Box sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 1.5, bgcolor: "#fff", ...extraSx }}>
      <Label>{label}</Label>
      <Value>{value}</Value>
    </Box>
  );

  const BlueAccentCell = ({ label, value }) => (
    <Box sx={{ border: "1px solid #bfdbfe", borderLeft: "4px solid #2563eb", borderRadius: "8px", p: 1.5, bgcolor: "#eff6ff" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#2563eb", mb: 0.5 }}>{label}</Typography>
      <Value>{value}</Value>
    </Box>
  );

  const GreenAccentCell = ({ label, value }) => (
    <Box sx={{ border: "1px solid #bbf7d0", borderLeft: "4px solid #16a34a", borderRadius: "8px", p: 1.5, bgcolor: "#f0fdf4" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <Typography sx={{ fontSize: "0.9rem" }}>📅</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#15803d" }}>{label}</Typography>
      </Box>
      <Value>{value}</Value>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {triage && (
            <Chip
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: triage.color }} />
                  <span>{triage.label}</span>
                </Box>
              }
              size="small"
              sx={{
                height: 22,
                fontSize: "0.75rem",
                fontWeight: 600,
                bgcolor: triage.bg,
                color: triage.color,
                border: `1px solid ${triage.border}`,
                borderRadius: "6px",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
            Updated: {new Date().toISOString().split("T")[0]}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<EditNoteIcon sx={{ fontSize: "0.85rem !important" }} />}
            onClick={onEdit}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: "7px",
              px: 1.75,
              py: 0.5,
              bgcolor: "#2563eb",
              boxShadow: "none",
              "&:hover": { bgcolor: "#1d4ed8", boxShadow: "none" },
            }}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={6}>
          <BlueAccentCell label="Working Diagnosis" value={data.working_diagnosis} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Cell label="Intervention Type" value={data.recommended_intervention} />
        </Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={6}>
          <Cell label="Treatment Goals" value={data.treatment_goals} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Cell label="Session Frequency" value={data.recommended_frequency} />
        </Grid>
      </Grid>

      {data.treatment_objectives && (
        <Box sx={{ mb: 1.5 }}>
          <Cell label="Treatment Objectives" value={data.treatment_objectives} />
        </Box>
      )}

      {data.follow_up_plan && (
        <Box sx={{ mb: 1.5 }}>
          <GreenAccentCell label="Follow-up Plan" value={data.follow_up_plan} />
        </Box>
      )}

      {data.additional_notes && (
        <Box sx={{ mb: 0 }}>
          <Cell label="Additional Notes" value={data.additional_notes} />
        </Box>
      )}
    </Box>
  );
};

// ─── Assessment Checkbox ──────────────────────────────────────────────────────
const AssessmentCheckbox = ({ assessmentKey, label, selected, onToggle }) => (
  <Box
    onClick={() => onToggle(assessmentKey)}
    sx={{
      border: `1px solid ${selected ? "#2563eb" : "#d1d5db"}`,
      borderRadius: "6px",
      px: 1.2,
      py: 0.55,
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      cursor: "pointer",
      bgcolor: selected ? "#eff6ff" : "white",
      transition: "all 0.1s",
      "&:hover": { borderColor: "#93c5fd", bgcolor: "#f0f7ff" },
    }}
  >
    <Checkbox
      checked={selected}
      onChange={() => onToggle(assessmentKey)}
      size="small"
      sx={{ p: 0, color: "#d1d5db", "&.Mui-checked": { color: "#2563eb" } }}
      onClick={(e) => e.stopPropagation()}
    />
    <Typography sx={{ fontSize: "0.77rem", color: "#374151", fontWeight: 500 }}>{label}</Typography>
  </Box>
);

// ─── Radio Option ─────────────────────────────────────────────────────────────
const RadioOption = ({ value, selected, label, desc, icon, color, bg, onChange }) => (
  <Box
    onClick={() => onChange(value)}
    sx={{
      flex: "1 1 200px",
      border: `1.5px solid ${selected ? color : "#e5e7eb"}`,
      borderRadius: "9px",
      px: 2,
      py: 1.25,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      mb: 3,
      cursor: "pointer",
      bgcolor: selected ? bg : "white",
      transition: "all 0.12s",
      "&:hover": { borderColor: color, bgcolor: bg },
    }}
  >
    <Radio
      checked={selected}
      onChange={() => onChange(value)}
      size="small"
      sx={{ p: 0, color: "#d1d5db", "&.Mui-checked": { color } }}
      onClick={(e) => e.stopPropagation()}
    />
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "7px",
        bgcolor: selected ? color : `${color}22`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: selected ? "white" : color,
        transition: "all 0.12s",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "#1f2937" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>{desc}</Typography>
    </Box>
  </Box>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
export default function Viewcase() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientInfo, appointment, mode } = location.state || {};

  const [formData, setFormData] = useState({
    chief_complaint: "", presenting_issue: "", medical_history: "", current_medications: "",
    allergies: "", family_history: "", social_history: "", mental_health_history: "",
    substance_use: "", previous_treatments: "", current_stressors: "", coping_mechanisms: "",
    trauma_history: "", patient_strengths: "",
    session_observations: "", clinical_impressions: "", therapist_notes: "",
    selected_assessments: [],
    session_summary: "", interventions_used: "", homework_assigned: "", plan_for_next_session: "",
    end_session_option: "book_next",
  });

  const [treatmentPlan, setTreatmentPlan] = useState({
    working_diagnosis: "", treatment_goals: "", recommended_intervention: "",
    recommended_frequency: "", treatment_objectives: "", follow_up_plan: "",
    additional_notes: "", triage_level: "",
  });
  const [treatmentPlanSaved, setTreatmentPlanSaved] = useState(false);
  const [treatmentModalOpen, setTreatmentModalOpen] = useState(false);
  const [futureReleaseOpen, setFutureReleaseOpen] = useState(false);

  const [errors, setErrors] = useState({ chief_complaint: false, presenting_issue: false });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeMainTab, setActiveMainTab] = useState("appointments");
  const [assessmentSection, setAssessmentSection] = useState("assigned");
  const [appointments, setAppointments] = useState([]);
  const [user, setUser] = useState(null);
  const [medicalDataExists, setMedicalDataExists] = useState(false);
  const [assignedAssessments, setAssignedAssessments] = useState([]);
  const [assessmentScores, setAssessmentScores] = useState({});
  const [treatmentHistory, setTreatmentHistory] = useState([]);
  const [customerData, setCustomerData] = useState(null);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [submitValidationError, setSubmitValidationError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const today = new Date().toISOString().split("T")[0];

  const formatDate = (d) => {
    if (!d) return "N/A";
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
  };
  const formatTime = (t) => {
    if (!t) return "N/A";
    const [h, m] = t.split(":");
    const dt = new Date(); dt.setHours(+h, +m);
    return dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  };
  const getStatusStyles = (s) => {
    switch (s?.toLowerCase()) {
      case "scheduled": return { color: "#1d4ed8", backgroundColor: "#eff6ff", borderColor: "#bfdbfe" };
      case "drafted": return { color: "#d97706", backgroundColor: "#fffbeb", borderColor: "#fde68a" };
      case "cancelled":
      case "canceled": return { color: "#b91c1c", backgroundColor: "#fef2f2", borderColor: "#fecaca" };
      case "completed": return { color: "#15803d", backgroundColor: "#f0fdf4", borderColor: "#bbf7d0" };
      case "not_attended": return { color: "#b91c1c", backgroundColor: "#fee2e2", borderColor: "#fecaca" };
      default: return { color: "#475569", backgroundColor: "#f1f5f9", borderColor: "#e2e8f0" };
    }
  };

  const handleAppointmentClick = (apt) => {
    if (!apt?.customer_id || !apt?.id) return;
    window.location.href = `/patient/view/${apt.customer_id}/${apt.id}`;
  };
  const getPatientInfo = (apt) => {
    const name = apt.customer_name || "Unknown";
    return { name, initials: name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase(), id: apt.customer_id, email: apt.customer_email };
  };

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) setUser(JSON.parse(u));
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    listAppointments({ dr_id: user.id })
      .then((d) => setAppointments(Array.isArray(d) ? d : []))
      .catch(() => setAppointments([]));
  }, [user?.id]);

  const patientId = patientInfo?.id || appointment?.customer_id;
  const appointmentId = appointment?.id;
  const isInCustomerMedical = !appointment?.is_new_case;
  let patientStatus = "NEW";
  if (isInCustomerMedical) patientStatus = "RETURNING";

  useEffect(() => {
    if (!patientId || !appointmentId) return;
    const load = async () => {
      setLoading(true); setError("");
      try {
        const med = await getPatientMedicalHistory(patientId, appointmentId);
        if (med && typeof med === "object") {
          setMedicalDataExists(!!med.id);
          setFormData((p) => ({
            ...p,
            chief_complaint: med.chief_complaint || "", presenting_issue: med.presenting_issue || "",
            medical_history: med.medical_history || "", current_medications: med.current_medications || "",
            allergies: med.allergies || "", family_history: med.family_history || "",
            social_history: med.social_history || "", mental_health_history: med.mental_health_history || "",
            substance_use: med.substance_use || "", previous_treatments: med.previous_treatments || "",
            current_stressors: med.current_stressors || "", coping_mechanisms: med.coping_mechanisms || "",
            trauma_history: med.trauma_history || "", patient_strengths: med.patient_strengths || "",
            created_at: med.created_at || "",
          }));
        }
        const clin = await getClinicalObservation(appointmentId);
        if (clin && typeof clin === "object") {
          setFormData((p) => ({
            ...p,
            session_observations: clin.session_observations || "",
            clinical_impressions: clin.clinical_impressions || "",
            therapist_notes: clin.therapist_notes || "",
            session_summary: clin.session_summary || "",
            interventions_used: clin.interventions_used || "",
            homework_assigned: clin.homework_assigned || "",
            plan_for_next_session: clin.plan_for_next_session || "",
            selected_assessments: clin.assessment_keys || [],
          }));
          if (clin.working_diagnosis) {
            setTreatmentPlan({
              working_diagnosis: clin.working_diagnosis || "",
              treatment_goals: clin.treatment_goals || "",
              recommended_intervention: clin.recommended_intervention || "",
              recommended_frequency: clin.recommended_frequency || "",
              treatment_objectives: clin.treatment_objectives || "",
              follow_up_plan: clin.follow_up_plan || "",
              additional_notes: clin.additional_notes || "",
              triage_level: clin.triage_level || "",
            });
            setTreatmentPlanSaved(true);
          }
        }
        try {
          const assigned = await getAssignedAssessmentsByCustomer(patientId);
          setAssignedAssessments(Array.isArray(assigned) ? assigned : []);
        } catch {
          // non-fatal
        }
        try {
          const cust = await getCustomer(patientId);
          if (cust?.id) setCustomerData(cust);
        } catch {
          // non-fatal
        }
        if (patientStatus === "RETURNING") {
          try {
            const hist = await getTreatmentHistory(patientId);
            setTreatmentHistory(Array.isArray(hist) ? hist : []);
          } catch {
            // non-fatal
          }
        }
      } catch (e) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [patientId, appointmentId]);

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: false }));
  };

  const handleAssessmentToggle = (key) => {
    setFormData((p) => ({
      ...p,
      selected_assessments: p.selected_assessments.includes(key)
        ? p.selected_assessments.filter((k) => k !== key)
        : [...p.selected_assessments, key],
    }));
  };

  const handleTreatmentPlanSave = (data) => {
    setTreatmentPlan(data);
    setTreatmentPlanSaved(true);
  };

  const handleSave = async (isDraft = false) => {
    if (patientStatus !== "RETURNING") {
      const newErr = { chief_complaint: !formData.chief_complaint.trim(), presenting_issue: !formData.presenting_issue.trim() };
      setErrors(newErr);
      if (newErr.chief_complaint || newErr.presenting_issue) return;
    }
    if (!patientId || !appointmentId) { setError("Patient or Appointment ID is missing"); return; }
    setSaving(true); setError("");
    try {
      if (patientStatus !== "RETURNING") {
        await savePatientMedicalHistory(patientId, normalizePayload({
          appointment_id: Number(appointmentId), customer_id: Number(patientId),
          ...Object.fromEntries(["chief_complaint", "presenting_issue", "medical_history", "current_medications", "allergies", "family_history", "social_history", "mental_health_history", "substance_use", "previous_treatments", "current_stressors", "coping_mechanisms", "trauma_history", "patient_strengths"].map((k) => [k, formData[k]])),
        }));
      }
      await saveClinicalObservation(appointmentId, normalizePayload({
        ...Object.fromEntries(["session_observations", "clinical_impressions", "therapist_notes", "session_summary", "interventions_used", "homework_assigned", "plan_for_next_session"].map((k) => [k, formData[k]])),
        assessment_keys: formData.selected_assessments,
        ...treatmentPlan,
        is_draft: isDraft,
      }));
      setMedicalDataExists(true);
      if (isDraft) {
        setSnackbar({ open: true, message: "Draft saved successfully!" });
      } else {
        setSnackbar({ open: true, message: "Case submitted successfully!" });
        setTimeout(() => navigate("/dashboard"), 1500);
      }
    } catch {
      setError("Failed to save data");
    } finally {
      setSaving(false);
    }
  };

  const requiredTreatmentPlanFields = [
    'working_diagnosis', 'treatment_goals', 'recommended_intervention',
    'recommended_frequency', 'treatment_objectives', 'follow_up_plan', 'triage_level',
  ];
  const isTreatmentPlanFullyFilled = () =>
    treatmentPlanSaved &&
    requiredTreatmentPlanFields.every((f) => treatmentPlan[f] && String(treatmentPlan[f]).trim() !== '');

  const handleOpenSubmitConfirm = () => {
    if (!isTreatmentPlanFullyFilled()) {
      setSubmitValidationError(
        "You need to complete the entire records including treatment plan before final submission. You can keep saving as draft. AFTER 72 HRS YOU WILL NOT BE ABLE TO ACCESS THE RECORDS FOR EITHER EDITING OR VIEWING."
      );
      return;
    }
    setSubmitValidationError("");
    setSubmitConfirmOpen(true);
  };
  const patientName = patientInfo?.name || appointment?.customer_name || "Unknown";

  // ── Time-based case state ──────────────────────────────────────────────────
  const sessionEndTime = React.useMemo(() => {
    if (!appointment?.slot_date || !appointment?.end_time) return null;
    const datePart = appointment.slot_date.split("T")[0];
    return new Date(`${datePart}T${appointment.end_time}`);
  }, [appointment]);

  const hoursAfterSession = sessionEndTime
    ? (Date.now() - sessionEndTime.getTime()) / 3_600_000
    : null;

  // No-show: session ended, status is still 'scheduled', and neither party has meeting activity
  const isNoShow =
    appointment?.status === "scheduled" &&
    hoursAfterSession !== null &&
    hoursAfterSession > 0 &&
    !appointment?.customer_state?.trim() &&
    !appointment?.counsellor_state?.trim();

  // No-show locked: 24 h have passed — record should be read-only, admin-only
  const isNoShowLocked = isNoShow && hoursAfterSession >= 24;

  // 72-hour draft warning: drafted but treatment plan never saved within 72 h
  const is72hWarning =
    appointment?.status === "drafted" &&
    hoursAfterSession !== null &&
    hoursAfterSession >= 72 &&
    !treatmentPlanSaved;

  // Case is locked from all edits
  const isCaseHistoryLocked =
    (medicalDataExists && appointment?.status === "completed") ||
    isNoShowLocked ||
    (appointment?.status === "drafted" && hoursAfterSession !== null && hoursAfterSession >= 72);

  // Show the action buttons (Save as Draft / Submit & Close)
  const hasBothStates = !!appointment?.customer_state?.trim() && !!appointment?.counsellor_state?.trim();
  const showActionButtons =
    !isCaseHistoryLocked && !isNoShowLocked && appointment?.status !== "completed" && hasBothStates;

  const medicalHistoryFields = [
    { key: "chief_complaint", label: "Chief Complaint", placeholder: "Primary reason for seeking treatment (e.g., Anxiety and panic attacks)", required: true, rows: 2, full: false },
    { key: "presenting_issue", label: "Presenting Issue", placeholder: "Detailed description of current symptoms, duration, frequency, and impact on daily life", required: true, rows: 2, full: false },
    { key: "medical_history", label: "Medical History", placeholder: "Past and current medical conditions", rows: 1 },
    { key: "current_medications", label: "Current Medications", placeholder: "List all current medications and dosages", rows: 1 },
    { key: "allergies", label: "Allergies", placeholder: "Any known allergies (medication, food, etc.)", rows: 1 },
    { key: "family_history", label: "Family History", placeholder: "Family mental health or medical history", rows: 1 },
    { key: "social_history", label: "Social History", placeholder: "Marital status, occupation, living situation, relationships, lifestyle...", rows: 2, full: false },
    { key: "mental_health_history", label: "Mental Health History", placeholder: "Previous mental health diagnoses or treatment...", rows: 1 },
    { key: "substance_use", label: "Substance Use", placeholder: "Alcohol, tobacco, drugs – type and frequency...", rows: 1 },
    { key: "previous_treatments", label: "Previous Treatments", placeholder: "Past therapy, hospitalizations, medications tried...", rows: 1 },
    { key: "current_stressors", label: "Current Stressors", placeholder: "Work, relationships, financial, health concerns...", rows: 1 },
    { key: "coping_mechanisms", label: "Coping Mechanisms", placeholder: "How does patient currently manage stress?", rows: 1 },
    { key: "trauma_history", label: "Trauma History", placeholder: "Any history of trauma, abuse, or significant adverse experiences...", rows: 1 },
    { key: "patient_strengths", label: "Patient Strengths", placeholder: "Support system, personal strengths, resilience factors, resources...", rows: 2, full: false, green: true },
  ];

  const therapeuticFields = [
    { key: "session_observations", label: "Session Observations", placeholder: "Initial observations about patient's presentation, behavior, mood...", rows: 2 },
    { key: "clinical_impressions", label: "Clinical Impressions", placeholder: "Initial clinical impressions and formulation...", rows: 2 },
    { key: "therapist_notes", label: "Therapist Notes", placeholder: "Additional notes, insights, or concerns...", rows: 2 },
  ];

  const assessments = {
    depression: [{ key: "bdi", label: "BDI" }, { key: "rses", label: "RSES" }, { key: "selsa", label: "SELSA" }],
    other: [{ key: "bpaq", label: "BPAQ" }, { key: "csi", label: "CSI" }, { key: "eri", label: "ERI" }, { key: "escq4s", label: "ESCQ 4S" }, { key: "gps", label: "GPS" }, { key: "gse", label: "GSE" }, { key: "csusbcc", label: "CSUSB CC" }, { key: "rdas", label: "RDAS" }],
  };

  if (loading) return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f7fb" }}>
      <CircularProgress sx={{ color: "#2563eb" }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6f9" }}>
      <Navbar />

      {/* Treatment Plan Modal */}
      <TreatmentPlanModal
        open={treatmentModalOpen}
        onClose={() => setTreatmentModalOpen(false)}
        initialData={treatmentPlan}
        onSave={handleTreatmentPlanSave}
        patientName={patientName}
      />

      {/* Submit Confirm Dialog */}
      <Dialog
        open={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", maxWidth: 420 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>
          Confirm Submission
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem", color: "#374151" }}>
            Are you sure you want to submit this case? Once submitted, <strong>no further modifications</strong> will be allowed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setSubmitConfirmOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: "8px", color: "#374151", borderColor: "#d1d5db" }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setSubmitConfirmOpen(false); handleSave(false); }}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: "8px", bgcolor: "#16a34a", boxShadow: "none", "&:hover": { bgcolor: "#15803d", boxShadow: "none" } }}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : "OK, Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Future Release Dialog */}
      <Dialog open={futureReleaseOpen} onClose={() => setFutureReleaseOpen(false)} PaperProps={{ sx: { borderRadius: "14px", p: 1, maxWidth: 360 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AssignmentIcon sx={{ fontSize: "1rem", color: "#2563eb" }} />
          </Box>
          Consultation
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.85rem", color: "#374151", mt: 0.5 }}>
            This feature is currently under development and will be available in a future release.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setFutureReleaseOpen(false)} variant="contained" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: "8px", bgcolor: "#2563eb", boxShadow: "none", "&:hover": { bgcolor: "#1d4ed8", boxShadow: "none" } }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Sticky Tab Bar */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e9ecef", position: "sticky", top: 70, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Tabs
          value={activeMainTab}
          onChange={(_, v) => setActiveMainTab(v)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "0.82rem", minHeight: 42, color: "#6b7280" },
            "& .Mui-selected": { color: "#2563eb !important", fontWeight: 600 },
            "& .MuiTabs-indicator": { bgcolor: "#2563eb" },
          }}
        >
          <Tab value="appointments" icon={<CalendarTodayIcon sx={{ fontSize: "0.95rem" }} />} iconPosition="start" label="Appointments" />
          <Tab value="assessments" icon={<AssignmentIcon sx={{ fontSize: "0.95rem" }} />} iconPosition="start" label="Assessments" />
          <Tab value="patient-details" icon={<PersonIcon sx={{ fontSize: "0.95rem" }} />} iconPosition="start" label="Patient Details" />
        </Tabs>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {activeMainTab === "assessments" && (
            <Box sx={{ display: "flex", bgcolor: "#f1f5f9", borderRadius: "8px", p: 0.35, gap: 0.35 }}>
              <Button size="small" onClick={() => setAssessmentSection("assigned")}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderRadius: "6px", px: 1.25, py: 0.4, minWidth: 0, bgcolor: assessmentSection === "assigned" ? "#fff" : "transparent", color: assessmentSection === "assigned" ? "#111827" : "#6b7280", boxShadow: assessmentSection === "assigned" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", "&:hover": { bgcolor: assessmentSection === "assigned" ? "#fff" : "#e2e8f0" } }}>
                Assigned
              </Button>
              <Button size="small" onClick={() => setAssessmentSection("library")}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderRadius: "6px", px: 1.25, py: 0.4, minWidth: 0, bgcolor: assessmentSection === "library" ? "#fff" : "transparent", color: assessmentSection === "library" ? "#111827" : "#6b7280", boxShadow: assessmentSection === "library" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", "&:hover": { bgcolor: assessmentSection === "library" ? "#fff" : "#e2e8f0" } }}>
                Library
              </Button>
            </Box>
          )}
          <Button
            variant="text"
            startIcon={<span style={{ fontSize: "1rem", lineHeight: 1 }}>←</span>}
            onClick={() => navigate("/dashboard")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              color: "#374151",
              borderRadius: "8px",
              px: 1.5,
              py: 0.5,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#f3f4f6" },
            }}
          >
            Back to Appointments
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1400, mx: "auto" }}>

        {activeMainTab === "assessments" && <AssessmentDetails hideNavbar hideOuterWrapper mainSection={assessmentSection} setMainSection={setAssessmentSection} />}

        {activeMainTab === "patient-details" && (
          <PatientDetails
            appointments={appointments} loading={loading} today={today}
            getPatientInfo={getPatientInfo} handleAppointmentClick={handleAppointmentClick}
            formatDate={formatDate} formatTime={formatTime} getStatusStyles={getStatusStyles}
          />
        )}

        {activeMainTab === "appointments" && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem" }}>{error}</Alert>}

            {/* ── No-Show Locked Banner (Rule 1) ── */}
            {isNoShowLocked && (
              <Box sx={{ mb: 2.5, bgcolor: "#1f2937", border: "1px solid #374151", borderRadius: "10px", px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <LockIcon sx={{ color: "#f9fafb", fontSize: "1.2rem", flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", color: "#f9fafb", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    CASE RECORD NOT AVAILABLE
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", mt: 0.25 }}>
                    This appointment was marked as No Show. The 24-hour edit window has expired. This record is read-only and can only be modified by an Admin.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ── No-Show (within 24 h) Banner (Rule 1) ── */}
            {isNoShow && !isNoShowLocked && (
              <Box sx={{ mb: 2.5, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <WarningAmberIcon sx={{ color: "#dc2626", fontSize: "1.2rem", flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#b91c1c" }}>
                    No Show — Session Ended
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#dc2626", mt: 0.25 }}>
                    The patient did not attend. You can still update this record for up to 72 hours after the session end time.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ── 72-Hour Treatment Plan Warning (Rule 5) ── */}
            {is72hWarning && (
              <Box sx={{ mb: 2.5, bgcolor: "#fffbeb", border: "2px solid #fbbf24", borderRadius: "10px", px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <WarningAmberIcon sx={{ color: "#d97706", fontSize: "1.4rem", flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", color: "#92400e", letterSpacing: "0.03em" }}>
                  YOU HAVEN'T UPDATED THE TREATMENT PLAN FOR THIS SESSION.
                </Typography>
              </Box>
            )}

            <Grid container spacing={2.5} alignItems="flex-start">

              {/* ══════════════════════════════════════════════════════════════
                  LEFT SIDEBAR — Patient Profile + Stats + Actions
              ══════════════════════════════════════════════════════════════ */}
              <Grid item xs={12} md={4} lg={3.5}>
                <Box sx={{ position: "sticky", top: 116, display: "flex", flexDirection: "column", gap: 2 }}>

                  {/* ── Patient Identity Card ── */}
                  <Card sx={{ borderRadius: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", border: "1px solid #e9ecef", overflow: "hidden" }}>
                    {/* Gradient header band */}
                    <Box sx={{ background: patientStatus === "RETURNING" ? "linear-gradient(135deg,#0891b2 0%,#0e7490 100%)" : "linear-gradient(135deg,#2563eb 0%,#1d4ed8 100%)", px: 2.5, pt: 2.5, pb: 4 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar sx={{ width: 52, height: 52, bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, fontSize: "1.1rem", border: "2px solid rgba(255,255,255,0.4)" }}>
                          {patientName?.split(" ")?.map((n) => n[0])?.join("")?.slice(0, 2)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff", lineHeight: 1.3 }}>{patientName}</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.4, flexWrap: "wrap" }}>
                            <Chip label={patientStatus} size="small" sx={{ height: 18, fontSize: "0.63rem", fontWeight: 700, bgcolor: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", "& .MuiChip-label": { px: 0.875 } }} />
                            {patientId && <Chip label={`ID: ${patientId}`} size="small" sx={{ height: 18, fontSize: "0.63rem", fontWeight: 600, bgcolor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.25)", fontFamily: "monospace", "& .MuiChip-label": { px: 0.875 } }} />}
                            {appointmentId && <Chip label={`Appt #${appointmentId}`} size="small" sx={{ height: 18, fontSize: "0.63rem", fontWeight: 600, bgcolor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.25)", fontFamily: "monospace", "& .MuiChip-label": { px: 0.875 } }} />}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <CardContent sx={{ pt: 0, px: 2, pb: "16px !important" }}>
                      {/* Demographics row — pulled up to overlap banner */}
                      <Box sx={{ bgcolor: "#fff", border: "1px solid #e9ecef", borderRadius: "10px", px: 1.75, py: 1.25, mt: -2, mb: 1.5, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                        {(customerData?.age || customerData?.dob || customerData?.gender || customerData?.marital_status) ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                            {(customerData?.age || customerData?.dob) && (
                              <Box>
                                <Typography sx={{ fontSize: "0.62rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Age</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1f2937" }}>{customerData.age || "—"}</Typography>
                              </Box>
                            )}
                            {customerData?.gender && (
                              <Box>
                                <Typography sx={{ fontSize: "0.62rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Gender</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1f2937" }}>{customerData.gender.charAt(0).toUpperCase() + customerData.gender.slice(1)}</Typography>
                              </Box>
                            )}
                            {customerData?.marital_status && (
                              <Box>
                                <Typography sx={{ fontSize: "0.62rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Marital</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1f2937" }}>{customerData.marital_status.charAt(0).toUpperCase() + customerData.marital_status.slice(1)}</Typography>
                              </Box>
                            )}
                            {customerData?.dob && (
                              <Box>
                                <Typography sx={{ fontSize: "0.62rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>DOB</Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1f2937" }}>{new Date(customerData.dob).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</Typography>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", fontStyle: "italic" }}>No demographic data</Typography>
                        )}
                      </Box>

                      {/* Appointment info */}
                      {appointment && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1.5 }}>
                          {appointment.appointment_date && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>Date</Typography>
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#1f2937" }}>{formatDate(appointment.appointment_date)}</Typography>
                            </Box>
                          )}
                          {appointment.appointment_time && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>Time</Typography>
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#1f2937" }}>{formatTime(appointment.appointment_time)}</Typography>
                            </Box>
                          )}
                          {appointment.status && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>Status</Typography>
                              <Chip label={appointment.status} size="small" sx={{ height: 18, fontSize: "0.63rem", fontWeight: 600, ...getStatusStyles(appointment.status), border: `1px solid ${getStatusStyles(appointment.status).borderColor}`, "& .MuiChip-label": { px: 1 } }} />
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Returning patient stats */}
                      {patientStatus === "RETURNING" && treatmentHistory.length > 0 && (() => {
                        const latestTriage = treatmentHistory[0]?.triage_level;
                        const triageStyle = {
                          green: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#16a34a", desc: "No Risk" },
                          amber: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", dot: "#d97706", desc: "Mild Case" },
                          red: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#dc2626", desc: "High Risk" },
                          black: { bg: "#f9fafb", color: "#1f2937", border: "#d1d5db", dot: "#374151", desc: "Critical" },
                        }[latestTriage] || null;
                        const lastVisitRaw = treatmentHistory[0]?.created_at;
                        const lastVisit = lastVisitRaw ? new Date(lastVisitRaw).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;
                        return (
                          <>
                            <Divider sx={{ mb: 1.5 }} />
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
                              {/* Stat pills */}
                              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
                                <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e9ecef", borderRadius: "8px", px: 1.25, py: 0.875, textAlign: "center" }}>
                                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#1f2937", lineHeight: 1 }}>{treatmentHistory.length}</Typography>
                                  <Typography sx={{ fontSize: "0.62rem", color: "#9ca3af", mt: 0.25 }}>Sessions</Typography>
                                </Box>
                                <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e9ecef", borderRadius: "8px", px: 1.25, py: 0.875, textAlign: "center" }}>
                                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1f2937", lineHeight: 1 }}>{lastVisit || "—"}</Typography>
                                  <Typography sx={{ fontSize: "0.62rem", color: "#9ca3af", mt: 0.25 }}>Last Visit</Typography>
                                </Box>
                              </Box>
                              {triageStyle && latestTriage && (
                                <Box sx={{ bgcolor: triageStyle.bg, border: `1px solid ${triageStyle.border}`, borderRadius: "8px", px: 1.25, py: 0.875, display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: triageStyle.dot, flexShrink: 0 }} />
                                  <Box>
                                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: triageStyle.color }}>
                                      {latestTriage.charAt(0).toUpperCase() + latestTriage.slice(1)} — {triageStyle.desc}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.6rem", color: "#9ca3af" }}>Latest triage</Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </>
                        );
                      })()}

                      {/* Consultation & Treatment Plan buttons */}
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                          <Button fullWidth variant="outlined" size="small"
                            startIcon={<AssignmentIcon sx={{ fontSize: "0.85rem !important" }} />}
                            onClick={() => setFutureReleaseOpen(true)}
                            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.78rem", py: 0.85, borderColor: "#2563eb", color: "#2563eb", justifyContent: "flex-start", "&:hover": { bgcolor: "#eff6ff" } }}
                          >
                            Consultation
                          </Button>
                          <Button fullWidth variant={treatmentPlanSaved ? "contained" : "outlined"} size="small"
                            startIcon={<LocalHospitalIcon sx={{ fontSize: "0.85rem !important" }} />}
                            onClick={() => setTreatmentModalOpen(true)}
                            sx={{
                              textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.78rem", py: 0.85, justifyContent: "flex-start", boxShadow: "none",
                              ...(treatmentPlanSaved
                                ? { bgcolor: "#16a34a", borderColor: "#16a34a", color: "#fff", "&:hover": { bgcolor: "#15803d", boxShadow: "none" } }
                                : { borderColor: "#16a34a", color: "#16a34a", "&:hover": { bgcolor: "#f0fdf4" } })
                            }}
                          >
                            {treatmentPlanSaved ? "Treatment Plan ✓" : "Add Treatment Plan"}
                          </Button>
                        </Box>
                      </>
                    </CardContent>
                  </Card>

                  {/* ── Action Buttons Card ── */}
                  {showActionButtons && (
                  <Card sx={{ borderRadius: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", border: "1px solid #e9ecef" }}>
                    <CardContent sx={{ p: "16px !important" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.25 }}>Session Actions</Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        <Button fullWidth variant="outlined" onClick={() => handleSave(true)} disabled={saving}
                          startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: "0.9rem !important" }} />}
                          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.82rem", py: 0.9, borderColor: "#d97706", color: "#d97706", justifyContent: "flex-start", "&:hover": { borderColor: "#b45309", bgcolor: "#fffbeb" } }}
                        >
                          Save as Draft
                        </Button>
                        <Button fullWidth variant="contained" onClick={handleOpenSubmitConfirm} disabled={saving}
                          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon sx={{ fontSize: "0.9rem !important" }} />}
                          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.82rem", py: 0.9, bgcolor: "#16a34a", boxShadow: "none", justifyContent: "flex-start", "&:hover": { bgcolor: "#15803d", boxShadow: "none" } }}
                        >
                          Submit & Close
                        </Button>
                        {submitValidationError && (
                          <Box sx={{ bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", px: 1.5, py: 1 }}>
                            <Typography sx={{ fontSize: "0.75rem", color: "#d97706", fontWeight: 600, lineHeight: 1.5 }}>
                              {submitValidationError.split("AFTER 72 HRS")[0]}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "#b91c1c", fontWeight: 700, lineHeight: 1.5, mt: 0.5 }}>
                              AFTER 72 HRS{submitValidationError.split("AFTER 72 HRS")[1]}
                            </Typography>
                          </Box>
                        )}
                        <Button fullWidth variant="text" onClick={() => navigate("/dashboard")}
                          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.78rem", py: 0.75, color: "#6b7280", justifyContent: "flex-start", "&:hover": { bgcolor: "#f3f4f6" } }}
                        >
                          ← Back to Appointments
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                  )}  {/* end showActionButtons */}

                </Box>
              </Grid>

              {/* ══════════════════════════════════════════════════════════════
                  RIGHT PANEL — Clinical Form Sections
              ══════════════════════════════════════════════════════════════ */}
              <Grid item xs={12} md={8} lg={8.5}>


                {/* ─────────────────────────────────────────────────────────────
                    Patient Health Record — only for returning patients
                ───────────────────────────────────────────────────────────── */}
                {patientStatus === "RETURNING" && medicalDataExists && (
                  <CollapsibleSection
                    title="Patient Health Record"
                    icon={<FolderSharedIcon sx={{ fontSize: "0.95rem" }} />}
                    accentColor="#0891b2"
                    badge="On Record"
                    defaultOpen={true}
                    rightContent={
                      formData.created_at ? (
                        <Chip
                          label={`Recorded: ${new Date(formData.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 500, bgcolor: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", "& .MuiChip-label": { px: 1 } }}
                        />
                      ) : null
                    }
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                      <LockIcon sx={{ fontSize: "0.75rem", color: "#9ca3af" }} />
                      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                        Initial case history recorded during first consultation.
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={2} columnSpacing={4}>
                      {medicalHistoryFields.map((f) => {
                        const val = formData[f.key];
                        const isEmpty = !val || (typeof val === "string" && val.trim() === "");
                        return (
                          <Grid item xs={12} md={f.full ? 12 : 6} key={f.key}>
                            <Box sx={{ py: 0.25 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: "0.78rem", color: "#6b7280", mb: 0.35 }}>
                                {f.label}
                              </Typography>
                              {isEmpty ? (
                                <Typography sx={{ fontSize: "0.82rem", color: "#c0c4cc", fontStyle: "italic" }}>—</Typography>
                              ) : (
                                <Typography sx={{ fontSize: "0.82rem", color: "#1f2937", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                                  {val}
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CollapsibleSection>
                )}

                {/* ─────────────────────────────────────────────────────────────
                    1. Initial Patient Case History
                    Locked (read-only) once saved; editable on first entry
                ───────────────────────────────────────────────────────────── */}
                {patientStatus !== "RETURNING" && <CollapsibleSection
                  title="Initial Patient Case History"
                  icon={<HealthAndSafetyIcon sx={{ fontSize: "0.95rem" }} />}
                  accentColor="#2563eb"
                  badge={isCaseHistoryLocked ? "Saved" : "Required"}
                  defaultOpen={false}
                >
                  {isCaseHistoryLocked ? (
                    <ReadOnlyMedicalHistory formData={formData} fields={medicalHistoryFields} />
                  ) : (
                    <>
                      <Typography sx={{ fontSize: "0.73rem", color: "#6b7280", mb: 1.75 }}>
                        Complete this form during the first consultation. This information will be saved permanently to the patient's record.
                      </Typography>
                      <Grid container rowSpacing={1} columnSpacing={2}>
                        {medicalHistoryFields.map((f) => (
                          <Grid item xs={12} md={f.full ? 12 : 6} key={f.key}>
                            {f.green ? (
                              <Box sx={{ bgcolor: "#f0fdf4", borderLeft: "3px solid #16a34a", borderRadius: "7px", p: 1.5 }}>
                                <FieldLabel text="Patient Strengths" />
                                <TextField
                                  fullWidth multiline rows={f.rows}
                                  placeholder={f.placeholder}
                                  value={formData[f.key]}
                                  onChange={(e) => handleChange(f.key, e.target.value)}
                                  sx={fieldSx(false, "#16a34a")}
                                />
                              </Box>
                            ) : (
                              <Box>
                                <FieldLabel text={f.label} required={f.required} />
                                <TextField
                                  fullWidth multiline rows={f.rows}
                                  placeholder={f.placeholder}
                                  value={formData[f.key]}
                                  onChange={(e) => handleChange(f.key, e.target.value)}
                                  error={!!errors[f.key]}
                                  helperText={errors[f.key] ? `${f.label} is required` : ""}
                                  sx={fieldSx(false)}
                                />
                              </Box>
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </CollapsibleSection>}

                {/* ─────────────────────────────────────────────────────────────
                    2. Therapeutic Notes — always editable, pre-filled from DB
                ───────────────────────────────────────────────────────────── */}
                {patientStatus !== "RETURNING" && <CollapsibleSection
                  title="Therapeutic Notes"
                  icon={<AssignmentIcon sx={{ fontSize: "0.95rem" }} />}
                  accentColor="#6d28d9"
                  defaultOpen={false}
                >
                  <Grid container rowSpacing={2} columnSpacing={3}>
                    {therapeuticFields.map((f) => (
                      <Grid item xs={12} key={f.key}>
                        <FieldLabel text={f.label} />
                        <TextField
                          fullWidth multiline rows={f.rows}
                          placeholder={f.placeholder}
                          value={formData[f.key]}
                          onChange={(e) => handleChange(f.key, e.target.value)}
                          sx={fieldSx(false, "#6d28d9")}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CollapsibleSection>}

                {/* ─────────────────────────────────────────────────────────────
                    3. Assign Assessments — shows scores if available,
                       pending state if assigned, empty state if none
                ───────────────────────────────────────────────────────────── */}
                <CollapsibleSection
                  title="Assign Assessments"
                  icon={<TaskAltIcon sx={{ fontSize: "0.95rem" }} />}
                  accentColor="#2563eb"
                  badge={formData.selected_assessments.length > 0 || assignedAssessments.length > 0
                    ? `${assignedAssessments.length + formData.selected_assessments.filter(k => !assignedAssessments.find(a => a.assessment_key === k)).length} total`
                    : null}
                  defaultOpen={false}
                >
                  {/* ── Assigned Assessments from DB (pending / completed) ── */}
                  {assignedAssessments.length > 0 && (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#d97706" }} />
                        <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151" }}>
                          Assigned assessments
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        {assignedAssessments.map((a) => {
                          const isCompleted = a.status === "completed";
                          const qData = QUESTIONNAIRES[a.assessment_key];
                          const SEV_COLORS = {
                            green:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", badgeBg: "#dcfce7" },
                            yellow: { bg: "#fefce8", color: "#a16207", border: "#fef08a", badgeBg: "#fef9c3" },
                            orange: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", badgeBg: "#ffedd5" },
                            red:    { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", badgeBg: "#fee2e2" },
                          };
                          const sev = (() => {
                            if (!isCompleted || a.obtained_score == null || !qData?.scoring) return null;
                            const range = qData.scoring.find((s) => a.obtained_score >= s.min && a.obtained_score <= s.max);
                            if (!range) return null;
                            return { ...range, ...(SEV_COLORS[range.color] || SEV_COLORS.orange) };
                          })();

                          // Category label + color
                          const cat = qData?.category || "other";
                          const catConfig = {
                            anxiety:    { label: "Anxiety",    bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
                            depression: { label: "Depression", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
                            other:      { label: "Other",      bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
                          }[cat] || { label: "Other", bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" };

                          const headerBg = sev ? sev.bg : isCompleted ? "#f0fdf4" : "#f8fafc";
                          const headerBorder = sev ? sev.border : isCompleted ? "#bbf7d0" : "#e5e7eb";
                          const headerColor = sev ? sev.color : isCompleted ? "#15803d" : "#374151";

                          return (
                            <Box key={a.id} sx={{ border: "1px solid " + headerBorder, borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>

                              {/* ── Top header: icon + name + status chip ── */}
                              <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25, bgcolor: headerBg, borderBottom: "1px solid " + headerBorder }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ fontSize: "1.4rem", lineHeight: 1 }}>{qData?.icon || "🧩"}</Box>
                                    <Box>
                                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                                        {qData?.name || (a.assessment_key || "").toUpperCase()}
                                      </Typography>
                                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: headerColor }}>
                                        {qData?.shortName || a.assessment_key}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Chip
                                    icon={isCompleted
                                      ? <CheckCircleIcon sx={{ fontSize: "0.75rem !important" }} />
                                      : <ScheduleIcon sx={{ fontSize: "0.75rem !important" }} />}
                                    label={isCompleted ? "Completed" : "Pending"}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: "0.65rem", fontWeight: 700, flexShrink: 0,
                                      bgcolor: isCompleted ? "#f0fdf4" : "#fffbeb",
                                      color: isCompleted ? "#15803d" : "#b45309",
                                      border: `1px solid ${isCompleted ? "#bbf7d0" : "#fde68a"}`,
                                      "& .MuiChip-label": { px: 0.875 },
                                      "& .MuiChip-icon": { color: isCompleted ? "#15803d" : "#b45309", ml: "5px" },
                                    }}
                                  />
                                </Box>

                                {/* Type chip + description */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                  <Chip label={catConfig.label} size="small" sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600, bgcolor: catConfig.bg, color: catConfig.color, border: "1px solid " + catConfig.border, "& .MuiChip-label": { px: 0.75 } }} />
                                  {qData?.description && (
                                    <Typography sx={{ fontSize: "0.68rem", color: "#6b7280", lineHeight: 1.4, flex: 1 }}>
                                      {qData.description}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              {/* ── Score / Value / Label for completed ── */}
                              {isCompleted && a.obtained_score != null ? (
                                <Box>
                                  {/* 3-column panel */}
                                  <Box sx={{ display: "flex", borderBottom: "1px solid " + headerBorder }}>
                                    {/* Score */}
                                    <Box sx={{ flex: 1, py: 1.25, px: 1.5, bgcolor: headerBg, borderRight: "1px solid " + headerBorder, textAlign: "center" }}>
                                      <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: headerColor, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4 }}>Score</Typography>
                                      <Typography sx={{ fontSize: "1.3rem", fontWeight: 900, color: headerColor, lineHeight: 1 }}>
                                        {a.obtained_score}
                                        <Box component="span" sx={{ fontSize: "0.7rem", fontWeight: 500, color: headerColor + "88", ml: 0.25 }}>/{a.total_score}</Box>
                                      </Typography>
                                    </Box>
                                    {/* Value */}
                                    <Box sx={{ flex: 1, py: 1.25, px: 1.5, bgcolor: headerBg, borderRight: "1px solid " + headerBorder, textAlign: "center" }}>
                                      <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: headerColor, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4 }}>Range</Typography>
                                      <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: headerColor, lineHeight: 1 }}>
                                        {sev ? `${sev.min}–${sev.max}` : "—"}
                                      </Typography>
                                    </Box>
                                    {/* Label */}
                                    <Box sx={{ flex: 2, py: 1.25, px: 1.5, bgcolor: headerBg, textAlign: "center" }}>
                                      <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: headerColor, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4 }}>Severity</Typography>
                                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, color: headerColor, lineHeight: 1.25 }}>
                                        {sev ? sev.level : "—"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {/* Recommendation banner */}
                                  {sev && (
                                    <Box sx={{ px: 1.75, py: 0.9, bgcolor: sev.badgeBg || sev.bg, display: "flex", alignItems: "center", gap: 0.75 }}>
                                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: sev.color, flexShrink: 0 }} />
                                      <Typography sx={{ fontSize: "0.72rem", color: sev.color, fontWeight: 600 }}>
                                        {sev.recommendation}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              ) : isCompleted ? (
                                <Box sx={{ px: 1.75, py: 1.25, bgcolor: "#f0fdf4" }}>
                                  <Typography sx={{ fontSize: "0.75rem", color: "#15803d", fontWeight: 500 }}>Assessment completed — score not recorded</Typography>
                                </Box>
                              ) : (
                                <Box sx={{ px: 1.75, py: 1.25, bgcolor: "#fffbeb", display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <ScheduleIcon sx={{ fontSize: "0.85rem", color: "#b45309" }} />
                                  <Typography sx={{ fontSize: "0.75rem", color: "#b45309", fontWeight: 500 }}>Awaiting patient response</Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}

                  {assignedAssessments.length === 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2.5, gap: 0.75 }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <AssignmentTurnedInIcon sx={{ fontSize: "1.1rem", color: "#d1d5db" }} />
                      </Box>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151" }}>No assessments assigned yet</Typography>
                    </Box>
                  )}

                  {/* ── Score Results (if scores exist) ── */}
                  {assessmentScores && Object.keys(assessmentScores).length > 0 && (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#2563eb" }} />
                        <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#374151" }}>
                          Assessment scores
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        {formData.selected_assessments
                          .filter((key) => assessmentScores[key])
                          .map((key) => {
                            const score = assessmentScores[key];
                            const pct = Math.round((score.value / score.max) * 100);
                            const barColor =
                              pct >= 70 ? "#22c55e" : pct >= 40 ? "#f59e0b" : "#ef4444";
                            return (
                              <Box
                                key={key}
                                sx={{
                                  border: "1px solid #e9ecef",
                                  borderRadius: "8px",
                                  p: 1.5,
                                  bgcolor: "#fff",
                                }}
                              >
                                <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af", mb: 0.4 }}>
                                  {score.category}
                                </Typography>
                                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1f2937", mb: 0.75 }}>
                                  {key.toUpperCase()}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                  <Typography sx={{ fontSize: "1.2rem", fontWeight: 600, color: "#1f2937" }}>
                                    {score.value}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                                    / {score.max}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    height: "4px",
                                    bgcolor: "#f1f5f9",
                                    borderRadius: "2px",
                                    mt: 0.75,
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{ height: "100%", width: `${pct}%`, bgcolor: barColor, borderRadius: "2px" }}
                                  />
                                </Box>
                                {score.severity && (
                                  <Chip
                                    label={score.severity}
                                    size="small"
                                    sx={{
                                      mt: 0.75,
                                      height: 18,
                                      fontSize: "0.65rem",
                                      fontWeight: 600,
                                      bgcolor:
                                        pct >= 70 ? "#f0fdf4" : pct >= 40 ? "#fffbeb" : "#fef2f2",
                                      color:
                                        pct >= 70 ? "#15803d" : pct >= 40 ? "#b45309" : "#b91c1c",
                                      border: `1px solid ${pct >= 70 ? "#bbf7d0" : pct >= 40 ? "#fde68a" : "#fecaca"
                                        }`,
                                      "& .MuiChip-label": { px: 0.875 },
                                    }}
                                  />
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    </>
                  )}

                  {/* ── Pending (assigned but no scores yet) ── */}

                </CollapsibleSection>



                {/* ─────────────────────────────────────────────────────────────
                    Treatment History — all past sessions (returning patients only)
                ───────────────────────────────────────────────────────────── */}
                {patientStatus === "RETURNING" && (
                  <CollapsibleSection
                    title="Treatment History"
                    icon={<HistoryIcon sx={{ fontSize: "0.95rem" }} />}
                    accentColor="#6d28d9"
                    badge={treatmentHistory.length > 0 ? `${treatmentHistory.length} session${treatmentHistory.length > 1 ? "s" : ""}` : null}
                    defaultOpen={false}
                  >
                    {treatmentHistory.length === 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 3, gap: 0.75 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <HistoryIcon sx={{ fontSize: "1.1rem", color: "#d1d5db" }} />
                        </Box>
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151" }}>No treatment history found</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {treatmentHistory.map((record, idx) => {
                          const triageColor = {
                            green: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
                            amber: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
                            red: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
                            black: { bg: "#f9fafb", color: "#1f2937", border: "#d1d5db" },
                          }[record.triage_level] || { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" };

                          const sessionDate = record.created_at
                            ? new Date(record.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                            : "Unknown date";

                          const fields = [
                            { label: "Working Diagnosis", value: record.working_diagnosis },
                            { label: "Treatment Goals", value: record.treatment_goals },
                            { label: "Treatment Objectives", value: record.treatment_objectives },
                            { label: "Recommended Intervention", value: record.recommended_intervention },
                            { label: "Recommended Frequency", value: record.recommended_frequency },
                            { label: "Follow Up Plan", value: record.follow_up_plan },
                            { label: "Session Observations", value: record.session_observations },
                            { label: "Clinical Impressions", value: record.clinical_impressions },
                            { label: "Therapist Notes", value: record.therapist_notes },
                            { label: "Session Summary", value: record.session_summary },
                            { label: "Interventions Used", value: record.interventions_used },
                            { label: "Homework Assigned", value: record.homework_assigned },
                            { label: "Plan for Next Session", value: record.plan_for_next_session },
                            { label: "Additional Notes", value: record.additional_notes },
                          ];

                          return (
                            <Box
                              key={record.appointment_id}
                              sx={{
                                border: "1px solid #e9ecef",
                                borderRadius: "10px",
                                overflow: "hidden",
                              }}
                            >
                              {/* Session header */}
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, bgcolor: "#fafbfc", borderBottom: "1px solid #e9ecef" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "#1f2937" }}>
                                    Session #{treatmentHistory.length - idx}
                                  </Typography>
                                  <Chip label={sessionDate} size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", "& .MuiChip-label": { px: 1 } }} />
                                  {record.triage_level && (() => {
                                    const td = { green: "No Risk", amber: "Mild Case", red: "High Risk", black: "Critical" }[record.triage_level] || "";
                                    return (
                                      <Chip
                                        label={
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: triageColor.color, flexShrink: 0 }} />
                                            {`${record.triage_level.charAt(0).toUpperCase() + record.triage_level.slice(1)}${td ? ` — ${td}` : ""}`}
                                          </Box>
                                        }
                                        size="small"
                                        sx={{ height: 18, fontSize: "0.65rem", fontWeight: 600, bgcolor: triageColor.bg, color: triageColor.color, border: `1px solid ${triageColor.border}`, "& .MuiChip-label": { px: 1 } }}
                                      />
                                    );
                                  })()}
                                </Box>
                                <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af" }}>Apt #{record.appointment_id}</Typography>
                              </Box>
                              {/* Fields grid */}
                              <Box sx={{ p: 2 }}>
                                <Grid container rowSpacing={1.25} columnSpacing={2}>
                                  {fields.map((f) => (
                                    <Grid item xs={12} md={6} key={f.label}>
                                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.35 }}>{f.label}</Typography>
                                      <Typography sx={{ fontSize: "0.82rem", color: f.value ? "#1f2937" : "#d1d5db", lineHeight: 1.6, whiteSpace: "pre-wrap", fontStyle: f.value ? "normal" : "italic" }}>{f.value || "—"}</Typography>
                                    </Grid>
                                  ))}
                                </Grid>
                                {/* Referral info */}
                                {(record.psychiatrist_name || record.hospital_name || record.referral_notes) && (
                                  <Box sx={{ mt: 1.5, p: 1.25, bgcolor: "#fef9ec", borderRadius: "8px", border: "1px solid #fde68a" }}>
                                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#b45309", textTransform: "uppercase", mb: 0.5 }}>Referral</Typography>
                                    {record.psychiatrist_name && <Typography sx={{ fontSize: "0.8rem", color: "#1f2937" }}>Psychiatrist: {record.psychiatrist_name}</Typography>}
                                    {record.hospital_name && <Typography sx={{ fontSize: "0.8rem", color: "#1f2937" }}>Hospital: {record.hospital_name}</Typography>}
                                    {record.referral_notes && <Typography sx={{ fontSize: "0.8rem", color: "#374151", mt: 0.5 }}>{record.referral_notes}</Typography>}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </CollapsibleSection>
                )}

                {/* ─────────────────────────────────────────────────────────────
                    5. First Consultation Notes — always editable, pre-filled from DB
                ───────────────────────────────────────────────────────────── */}
                {patientStatus !== "RETURNING" && <CollapsibleSection
                  title="First Consultation Notes"
                  icon={<FolderSharedIcon sx={{ fontSize: "0.95rem" }} />}
                  accentColor="#2563eb"
                  defaultOpen={false}
                >
                  <Grid container rowSpacing={1} columnSpacing={2}>
                    <Grid item xs={12}>
                      <FieldLabel text="Session Summary" />
                      <TextField
                        fullWidth multiline rows={2}
                        placeholder="Summary of what was discussed during the first consultation..."
                        value={formData.session_summary}
                        onChange={(e) => handleChange("session_summary", e.target.value)}
                        sx={fieldSx(false)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Interventions Used" />
                      <TextField
                        fullWidth
                        placeholder="e.g., Psychoeducation, rapport building..."
                        value={formData.interventions_used}
                        onChange={(e) => handleChange("interventions_used", e.target.value)}
                        sx={fieldSx(false)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FieldLabel text="Homework Assigned" />
                      <TextField
                        fullWidth
                        placeholder="Tasks assigned to patient..."
                        value={formData.homework_assigned}
                        onChange={(e) => handleChange("homework_assigned", e.target.value)}
                        sx={fieldSx(false)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FieldLabel text="Plan for Next Session" />
                      <TextField
                        fullWidth multiline rows={2}
                        placeholder="What to focus on in the next session..."
                        value={formData.plan_for_next_session}
                        onChange={(e) => handleChange("plan_for_next_session", e.target.value)}
                        sx={fieldSx(false)}
                      />
                    </Grid>
                  </Grid>
                </CollapsibleSection>}



              </Grid>{/* end right panel */}
            </Grid>{/* end outer grid */}
          </>
        )}
      </Box>
    </Box>
  );
}